import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';

interface NavigationItemData {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
}

interface NavigationItemProps {
  item: NavigationItemData;
  currentPath: string;
  onClick: () => void;
  collapsed?: boolean;
}

const NavigationItem: React.FC<NavigationItemProps> = ({
  item,
  currentPath,
  onClick,
  collapsed = false,
}) => {
  const theme = useTheme();

  // Determinar si el elemento está activo
  const isActive = currentPath === item.path ||
    (item.path !== '/dashboard' && currentPath.startsWith(item.path));

  const iconNode = item.badge && item.badge > 0 ? (
    <Badge
      badgeContent={item.badge}
      color="error"
      max={99}
      sx={{
        '& .MuiBadge-badge': {
          fontSize: '0.75rem',
          height: 18,
          minWidth: 18,
        },
      }}
    >
      {item.icon}
    </Badge>
  ) : (
    item.icon
  );

  const button = (
    <ListItemButton
      onClick={onClick}
      sx={{
        mx: 1,
        borderRadius: 2,
        mb: 0.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 1 : 2,
        backgroundColor: isActive
          ? alpha(theme.palette.primary.main, 0.12)
          : 'transparent',
        color: isActive
          ? theme.palette.primary.main
          : theme.palette.text.primary,
        '&:hover': {
          backgroundColor: isActive
            ? alpha(theme.palette.primary.main, 0.16)
            : alpha(theme.palette.action.hover, 0.04),
        },
        '&.Mui-focusVisible': {
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
        },
      }}
    >
      <ListItemIcon
        sx={{
          color: 'inherit',
          minWidth: collapsed ? 'auto' : 40,
          justifyContent: 'center',
        }}
      >
        {iconNode}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: isActive ? 600 : 400,
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding>
      {collapsed ? (
        <Tooltip title={item.label} placement="right" arrow>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
};

export default NavigationItem;
